import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { API } from "@/App";
import { 
  ArrowLeft, Route, Trophy, Beer, CheckCircle2, MapPin, Calendar, Crown, Loader2, Share2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const LOGO_URL = "https://res.cloudinary.com/dmivaa0e0/image/upload/v1772834626/branding/logo_badge.png";

const PublicProfile = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard/user/${userId}/profile`);
      setProfile(response.data);
    } catch (err) {
      setError("Rider not found");
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'R';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile?.name}'s Rider Profile`,
          text: `Check out this rider's stats on Rides & Dives!`,
          url: shareUrl
        });
      } catch (e) {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Profile link copied!");
    }
  };

  if (loading) {
    return (
      <div className="mobile-container asphalt-texture min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-bronze" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="mobile-container asphalt-texture min-h-screen flex flex-col items-center justify-center p-4">
        <div className="text-6xl mb-4">🏍️</div>
        <h1 className="font-oswald text-2xl font-bold uppercase mb-2">Rider Not Found</h1>
        <p className="text-gray-500 text-center mb-6">This rider may have gone off the grid.</p>
        <Button onClick={() => navigate('/')} className="bg-bronze hover:bg-bronze-600">
          Back to Home
        </Button>
      </div>
    );
  }

  const stats = profile.stats || {};

  return (
    <div className="mobile-container asphalt-texture min-h-screen pb-8">
      {/* Header */}
      <div className="p-4 pt-6">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <img src={LOGO_URL} alt="Rides & Dives" className="h-10" />
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={handleShare}
          >
            <Share2 className="w-5 h-5" />
          </Button>
        </div>

        {/* Profile Card */}
        <div className="glass rounded-xl p-6 mb-6 text-center">
          <Avatar className="w-24 h-24 border-4 border-bronze mx-auto mb-4">
            <AvatarImage src={profile.picture} />
            <AvatarFallback className="bg-bronze/20 text-bronze font-oswald text-3xl">
              {getInitials(profile.name)}
            </AvatarFallback>
          </Avatar>
          
          <h1 className="font-oswald text-2xl font-bold uppercase">{profile.name}</h1>
          {profile.username && (
            <p className="text-bronze">@{profile.username}</p>
          )}
          {profile.is_premium && (
            <div className="flex items-center justify-center gap-1 mt-2">
              <Crown className="w-4 h-4 text-yellow-500" />
              <span className="text-yellow-500 text-xs font-bold uppercase">Premium Rider</span>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="glass rounded-xl p-4 text-center">
            <Route className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <div className="text-3xl font-oswald font-bold">{stats.total_miles || 0}</div>
            <div className="text-xs text-gray-500 uppercase">Miles Logged</div>
          </div>
          <div className="glass rounded-xl p-4 text-center">
            <Trophy className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
            <div className="text-3xl font-oswald font-bold">{stats.total_rides || 0}</div>
            <div className="text-xs text-gray-500 uppercase">Rides Completed</div>
          </div>
          <div className="glass rounded-xl p-4 text-center">
            <Beer className="w-8 h-8 text-amber-400 mx-auto mb-2" />
            <div className="text-3xl font-oswald font-bold">{stats.total_bars || 0}</div>
            <div className="text-xs text-gray-500 uppercase">Dives Visited</div>
          </div>
          <div className="glass rounded-xl p-4 text-center">
            <CheckCircle2 className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
            <div className="text-3xl font-oswald font-bold">{stats.total_checkins || 0}</div>
            <div className="text-xs text-gray-500 uppercase">Check-ins</div>
          </div>
        </div>

        {/* Recent Rides */}
        {profile.recent_rides && profile.recent_rides.length > 0 && (
          <div className="mb-6">
            <h2 className="font-oswald text-lg uppercase mb-3 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              Recent Rides
            </h2>
            <div className="space-y-2">
              {profile.recent_rides.map((ride, idx) => (
                <div key={idx} className="glass rounded-xl p-4 flex items-center gap-3">
                  {ride.cover_image ? (
                    <img src={ride.cover_image} alt={ride.trip_title} className="w-12 h-12 rounded-lg object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-bronze/20 flex items-center justify-center">
                      <Route className="w-6 h-6 text-bronze" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{ride.trip_title}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="text-green-400">{Math.round(ride.miles)} mi</span>
                      <span>•</span>
                      <span>{formatDate(ride.completed_at)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Dives */}
        {profile.recent_bars && profile.recent_bars.length > 0 && (
          <div className="mb-6">
            <h2 className="font-oswald text-lg uppercase mb-3 flex items-center gap-2">
              <Beer className="w-5 h-5 text-amber-400" />
              Dives Visited
            </h2>
            <div className="space-y-2">
              {profile.recent_bars.map((bar, idx) => (
                <div key={idx} className="glass rounded-xl p-4 flex items-center gap-3 border-amber-500/20">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-xl">
                    🍺
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{bar.stop_name}</p>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{bar.address || 'Unknown location'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="glass rounded-xl p-6 text-center">
          <p className="text-gray-400 mb-4">Want to track your own rides?</p>
          <Button 
            className="bg-bronze hover:bg-bronze-600 font-oswald uppercase"
            onClick={() => navigate('/')}
          >
            Join Rides & Dives
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PublicProfile;
